import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Ban, Loader2, ChevronLeft, UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

/**
 * SettingsPrivacyPage — currently focuses on the Blocked Members list.
 * Future Settings sub-pages can mount alongside this under /settings/*.
 */
export default function SettingsPrivacyPage() {
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblockingId, setUnblockingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/safety/blocked');
      setBlocked(res.data.blocked || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load blocked list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unblock = async (entry) => {
    setUnblockingId(entry.blocked_user_id);
    try {
      await api.delete(`/safety/block/${entry.blocked_user_id}`);
      setBlocked((prev) => prev.filter((e) => e.blocked_user_id !== entry.blocked_user_id));
      toast.success(`${entry.blocked_user_name || 'Member'} unblocked`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unblock');
    } finally {
      setUnblockingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-cream font-body">
      <header className="bg-white border-b border-border-subtle sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="font-display text-xl text-navy">
            Cali<span className="italic-teal">Moms</span> Connect
          </Link>
          <Link to="/dashboard" className="text-teal text-sm font-medium inline-flex items-center gap-1 hover:text-teal-deep">
            <ChevronLeft size={14} /> Dashboard
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-teal/10 flex items-center justify-center">
            <Lock className="text-teal" size={22} />
          </div>
          <div>
            <h1 className="font-display text-3xl text-navy" data-testid="settings-privacy-title">Privacy</h1>
            <p className="text-text-mid text-sm mt-1">Manage who can see and contact you on CMC.</p>
          </div>
        </div>

        <section className="bg-white rounded-2xl shadow-card p-6 border border-border-subtle" data-testid="blocked-members-section">
          <div className="flex items-center gap-2 mb-4">
            <Ban className="text-text-mid" size={18} />
            <h2 className="font-display text-lg text-navy">Blocked Members</h2>
            <span className="text-xs text-text-light">({blocked.length})</span>
          </div>

          {loading ? (
            <p className="text-text-mid text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </p>
          ) : blocked.length === 0 ? (
            <div className="text-center py-10" data-testid="blocked-empty">
              <UserX className="text-text-light mx-auto mb-2" size={28} />
              <p className="text-text-mid text-sm">You haven't blocked anyone.</p>
              <p className="text-text-light text-xs mt-1">If anything feels off, use the ⋯ menu on a member card to Report or Block.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {blocked.map((b) => (
                <li
                  key={b.blocked_user_id}
                  className="py-3 flex items-center justify-between gap-3"
                  data-testid={`blocked-row-${b.blocked_user_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy">{b.blocked_user_name || 'Unknown member'}</p>
                    <p className="text-[11px] text-text-light">
                      Blocked {b.created_at ? new Date(b.created_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => unblock(b)}
                    disabled={unblockingId === b.blocked_user_id}
                    className="rounded-full"
                    data-testid={`unblock-${b.blocked_user_id}`}
                  >
                    {unblockingId === b.blocked_user_id ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                    Unblock
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <p className="text-xs text-text-light text-center mt-6">
          Reports and other safety actions are reviewed by our team within 24 hours.
        </p>
      </div>
    </div>
  );
}
